import React from 'react';
import styled from 'styled-components';
import { useGetApiRequestLogQuery } from '../../store/api/logsApi.ts';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalTitle, 
  ModalBody,
  CloseButton,
  Badge,
  LoadingSpinner,
  Alert
} from '../../styles/components.ts';
import { formatDateTime } from '../../utils/formatting.tsx';
import { 
  XMarkIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  UserIcon,
  ClockIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const DetailSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DetailItem = styled.div``;

const DetailLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const DetailValue = styled.div`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[900]};
  word-break: break-all;
`;

const DetailValueCode = styled(DetailValue)`
  font-family: monospace;
  background-color: ${props => props.theme.colors.gray[100]};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const StatusBadge = styled.span<{ $status: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-family: monospace;
  
  svg {
    width: 1rem;
    height: 1rem;
  }
  
  ${props => {
    if (props.$status >= 200 && props.$status < 300) {
      return `
        background-color: ${props.theme.colors.success[50]};
        color: ${props.theme.colors.success[600]};
      `;
    } else if (props.$status >= 400 && props.$status < 500) {
      return `
        background-color: ${props.theme.colors.warning[50]};
        color: ${props.theme.colors.warning[600]};
      `;
    } else if (props.$status >= 500) {
      return `
        background-color: ${props.theme.colors.error[50]};
        color: ${props.theme.colors.error[600]};
      `;
    } else {
      return `
        background-color: ${props.theme.colors.gray[50]};
        color: ${props.theme.colors.gray[600]};
      `;
    }
  }}
`;

const MethodBadge = styled.span<{ $method: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-family: monospace;
  
  ${props => {
    switch (props.$method) {
      case 'GET':
        return `
          background-color: ${props.theme.colors.primary[50]};
          color: ${props.theme.colors.primary[600]};
        `;
      case 'POST':
        return `
          background-color: ${props.theme.colors.success[50]};
          color: ${props.theme.colors.success[600]};
        `;
      case 'PUT':
        return `
          background-color: ${props.theme.colors.warning[50]};
          color: ${props.theme.colors.warning[600]};
        `;
      case 'DELETE':
        return `
          background-color: ${props.theme.colors.error[50]};
          color: ${props.theme.colors.error[600]};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[50]};
          color: ${props.theme.colors.gray[600]};
        `;
    }
  }}
`;

const ExecutionTime = styled.span<{ $slow?: boolean }>`
  font-family: monospace;
  ${props => props.$slow && `
    color: ${props.theme.colors.error[600]};
    font-weight: ${props.theme.fontWeights.semibold};
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
`;

const ErrorContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

interface LogDetailModalProps {
  logId: string;
  isOpen: boolean;
  onClose: () => void;
}

const getStatusIcon = (statusCode: number) => {
  if (statusCode >= 200 && statusCode < 300) {
    return <CheckCircleIcon />;
  } else if (statusCode >= 400) {
    return <ExclamationTriangleIcon />;
  }
  return null;
};

export function LogDetailModal({ logId, isOpen, onClose }: LogDetailModalProps) {
  const { data: log, isLoading, error } = useGetApiRequestLogQuery(logId, {
    skip: !isOpen || !logId
  });

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <Modal $isOpen>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </ModalContent>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal $isOpen>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Error</ModalTitle>
            <CloseButton onClick={onClose}>
              <XMarkIcon />
            </CloseButton>
          </ModalHeader>
          <ErrorContainer>
            <Alert variant="error">
              Failed to load request details. Please try again.
            </Alert>
          </ErrorContainer>
        </ModalContent>
      </Modal>
    );
  }

  if (!log) {
    return null;
  }

  return (
    <Modal $isOpen>
      <ModalOverlay onClick={onClose} />
      <ModalContent style={{ maxWidth: '800px' }}>
        <ModalHeader>
          <ModalTitle>API Request Details</ModalTitle>
          <CloseButton onClick={onClose}>
            <XMarkIcon />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <DetailSection>
            <SectionTitle>
              <GlobeAltIcon />
              Request Information
            </SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Timestamp</DetailLabel>
                <DetailValue>{formatDateTime(log.timestamp)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>HTTP Method</DetailLabel>
                <DetailValue>
                  <MethodBadge $method={log.httpMethod}>
                    {log.httpMethod}
                  </MethodBadge>
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Status Code</DetailLabel>
                <DetailValue>
                  <StatusBadge $status={log.statusCode}>
                    {getStatusIcon(log.statusCode)}
                    {log.statusCode}
                  </StatusBadge>
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Execution Time</DetailLabel>
                <DetailValue>
                  <ExecutionTime $slow={log.executionTimeMs > 1000}>
                    {log.executionTimeMs}ms
                  </ExecutionTime>
                </DetailValue>
              </DetailItem>
              {log.errorMessage && log.errorMessage.length > 0 && (
                <DetailItem style={{ gridColumn: '1 / -1' }}>
                  <DetailLabel>Error Message</DetailLabel>
                  <DetailValue>
                    <div style={{
                      backgroundColor: '#FEF2F2',
                      color: '#991B1B',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      borderLeft: '4px solid #DC2626',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      wordBreak: 'break-word'
                    }}>
                      {log.errorMessage}
                    </div>
                  </DetailValue>
                </DetailItem>
              )}
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>Endpoint</DetailLabel>
                <DetailValueCode>{log.endpoint}</DetailValueCode>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Controller</DetailLabel>
                <DetailValueCode>{log.controller}</DetailValueCode>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Action</DetailLabel>
                <DetailValueCode>{log.action}</DetailValueCode>
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <SectionTitle>
              <UserIcon />
              Client Information
            </SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Client ID</DetailLabel>
                <DetailValueCode>{log.clientId}</DetailValueCode>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Organization ID</DetailLabel>
                <DetailValueCode>{log.organizationId}</DetailValueCode>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Provider</DetailLabel>
                <DetailValue>{log.providerName}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <SectionTitle>
              <ClockIcon />
              Performance Information
            </SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Execution Time</DetailLabel>
                <DetailValue>
                  <ExecutionTime $slow={log.executionTimeMs > 1000}>
                    {log.executionTimeMs}ms
                  </ExecutionTime>
                  {log.executionTimeMs > 1000 && (
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: '#dc2626', 
                      marginTop: '0.25rem' 
                    }}>
                      This request took longer than expected (&gt;1000ms)
                    </div>
                  )}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Request Status</DetailLabel>
                <DetailValue>
                  {log.statusCode >= 200 && log.statusCode < 300 ? (
                    <span style={{ color: '#166534' }}>✓ Successful</span>
                  ) : log.statusCode >= 400 && log.statusCode < 500 ? (
                    <span style={{ color: '#92400e' }}>⚠ Client Error</span>
                  ) : log.statusCode >= 500 ? (
                    <span style={{ color: '#991b1b' }}>✗ Server Error</span>
                  ) : (
                    <span style={{ color: '#374151' }}>Unknown</span>
                  )}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </DetailSection>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}